/** React */
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

/** MUI icons */
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/** MUI imports */
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
    Box,
    FormControlLabel,
    Grid,
    InputAdornment,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

/** Component imports */
import { Additionals } from "./";

/** Component styles */
const PREFIX = "Recurring";

const classes = {
    switch: `${PREFIX}-switch`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    backgroundColor: "#F8F9F8",
    marginTop: 16,
    padding: 30,
    borderRadius: 30,

    [`& .${classes.switch}`]: {
        "& .MuiSwitch-switchBase .MuiSwitch-thumb": {
            color: "#FFF",
        },
        "& .MuiSwitch-switchBase + .MuiSwitch-track": {
            backgroundColor: "#031851",
        },
        "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
            color: "#FF5968",
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#FFA8B1",
        },
    },
}));

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)({
    backgroundColor: "#F8F9F8",
    boxShadow: "none",
    "&:before": {
        display: "none",
    },
});

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#031851" }} />} {...props} />
))({
    "& .MuiAccordionSummary-content": {
        margin: "12px 0",
    },
});

const AccordionDetails = styled(MuiAccordionDetails)({
    padding: "0px 0px 20px 0px",
    "& .MuiCard-root": {
        boxShadow: "none",
    },
});

export const Recurring = (props) => {
    // Properties
    const { clientMarket, valuesContRecu, setValuesContRecu, setShow, setTexto, setAutoAlertType, isError,isErrorFocus, setIsErrorFocus } =
        props;
        
    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Global variables */
    const sumTotal =
        parseFloat(valuesContRecu.tothExe) +
        parseFloat(valuesContRecu.tothCons) +
        parseFloat(valuesContRecu.tothRep) +
        parseFloat(valuesContRecu.tothOthers);

    const checks =
        valuesContRecu.checkedExe ||
        valuesContRecu.checkedCons ||
        valuesContRecu.checkedRep ||
        valuesContRecu.checkedOthers;

    /** Component functions */
    useEffect(() => {
        setValuesContRecu({
            ...valuesContRecu,
            recuChecks: checks,
            recuHours: sumTotal,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientMarket, sumTotal, checks]);

    const handleChangeSwitchHourly = (event) => {
        setValuesContRecu({
            ...valuesContRecu,
            recuBaseRate: event.target.checked
                ? 0
                : valuesContRecu.payAnnually
                ? valuesContRecu.recuOriginalFee * 12
                : valuesContRecu.recuOriginalFee,
            recuDiscount: !event.target.checked && valuesContRecu.payAnnually ? 15 : 0,
            recuProjectTotal: event.target.checked
                ? 0
                : valuesContRecu.payAnnually
                ? valuesContRecu.recuOriginalFee * 12 * 0.85
                : valuesContRecu.recuOriginalFee,
            recuAdditionalsDiscount: !event.target.checked && valuesContRecu.payAnnually ? 15 : 0,
            recuAdditionalsOnly: event.target.checked,
            checkedCont: event.target.checked,
            tothCont: 0,
            startDateCont: event.target.checked ? valuesContRecu.initDate : null,
            finishDateCont: null,
            isError:false
        });
        setIsErrorFocus(false)
    };

    const handleChangeSwitchPay = (event) => {
        setValuesContRecu({
            ...valuesContRecu,
            recuBaseRate: valuesContRecu.recuAdditionalsOnly
                ? valuesContRecu.recuBaseRate
                : event.target.checked
                ? valuesContRecu.recuOriginalFee * 12
                : valuesContRecu.recuOriginalFee,
            recuDiscount: valuesContRecu.recuAdditionalsOnly ? 0 : event.target.checked ? 15 : 0,
            recuProjectTotal: valuesContRecu.recuAdditionalsOnly
                ? valuesContRecu.recuBaseRate
                : event.target.checked
                ? valuesContRecu.recuOriginalFee * 12 * 0.85
                : valuesContRecu.recuOriginalFee,
            recuAdditionalsDiscount: valuesContRecu.recuAdditionalsOnly ? 0 : event.target.checked ? 15 : 0,
            payAnnually: event.target.checked,
            isError:false
        });
        setIsErrorFocus(false)
    };

    const handleChangeSinceDate = (date) => {
        if (date === null) {
            setValuesContRecu({
                ...valuesContRecu,
                initDate: date,
                recuAdditionalsOnly: false,
                checkedCont: false,
                tothCont: 0,
                startDateCont: null,
                finishDateCont: null,
            });
        } else {
            setValuesContRecu({
                ...valuesContRecu,
                initDate: moment(date).startOf("month"),
                isError:false
            });
        }
        setIsErrorFocus(false)
    };

    const handleChangeDiscount = (event) => {
        let discountAux =
            event.target.value >= 0 && event.target.value <= 100 ? event.target.value : valuesContRecu.recuDiscount;

        setValuesContRecu({
            ...valuesContRecu,
            recuDiscount: discountAux,
            recuAdditionalsDiscount: discountAux,
            recuProjectTotal: valuesContRecu.recuAdditionalsOnly
                ? (valuesContRecu.tothCont ?? 0) * valuesContRecu.recuHourRate * (1 - discountAux / 100)
                : valuesContRecu.recuBaseRate * (1 - discountAux / 100),
                isError:false
        });
        setIsErrorFocus(false)
    };

    return (
        <StyledContainer>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={t("language.locale")}
                localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography
                            variant="h1"
                            color="primary"
                            sx={{
                                display: "inline-block",
                                fontWeight: 700,
                                pr: 6,
                            }}
                        >
                            {t(`invoicing.recurring`)}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={valuesContRecu.recuAdditionalsOnly}
                                    className={classes.switch}
                                    color="secondary"
                                    disabled={!valuesContRecu.initDate}
                                    inputProps={{ "aria-label": "controlled" }}
                                    name="payAnnuallyRecu"
                                    onChange={handleChangeSwitchHourly}
                                />
                            }
                            label={
                                <Typography variant="body2" align="center" sx={{ lineHeight: 0 }}>
                                    {t("proposals.hourly")}
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={valuesContRecu.payAnnually}
                                    className={classes.switch}
                                    color="secondary"
                                    inputProps={{ "aria-label": "controlled" }}
                                    name="payAnnuallyRecu"
                                    onChange={handleChangeSwitchPay}
                                />
                            }
                            label={
                                <Typography variant="body2" align="center" sx={{ lineHeight: 0 }}>
                                    {t("proposals.payAnnually")}
                                </Typography>
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Accordion
                            TransitionProps={{ unmountOnExit: true }}
                            sx={{
                                borderBottom: "3px solid #ff5968",
                            }}
                        >
                            <AccordionSummary aria-controls="panel-content" id="panel-header">
                                <Typography
                                    variant="h2"
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                    }}
                                >
                                    {t("invoicing.description")}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <List>
                                    <ListItem disableGutters alignItems="center">
                                        <ListItemIcon sx={{ minWidth: 35 }}>
                                            <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1">
                                                    <span style={{ fontWeight: 600 }}>{t("proposals.contrDed")} </span>
                                                    - {t("proposals.aPubAccCont")}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem disableGutters alignItems="center">
                                        <ListItemIcon sx={{ minWidth: 35 }}>
                                            <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1">
                                                    <span style={{ fontWeight: 600 }}>{t("proposals.arqFinanCont")} </span>
                                                    - {t("proposals.arqFinanContDet")}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem disableGutters alignItems="center">
                                        <ListItemIcon sx={{ minWidth: 35 }}>
                                            <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1">
                                                    <span style={{ fontWeight: 600 }}>{t("proposals.interContFin")} </span>
                                                    - {t("proposals.interContFinDet")}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem disableGutters alignItems="center">
                                        <ListItemIcon sx={{ minWidth: 35 }}>
                                            <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1">
                                                    <span style={{ fontWeight: 600 }}>{t("proposals.politProc")} </span>
                                                    - {t("proposals.politProcDet")}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem disableGutters alignItems="center">
                                        <ListItemIcon sx={{ minWidth: 35 }}>
                                            <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1">
                                                    <span style={{ fontWeight: 600 }}>{t("proposals.addPerRep")} </span>
                                                    - {t("proposals.addTayMade")}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem disableGutters alignItems="center">
                                        <ListItemIcon sx={{ minWidth: 35 }}>
                                            <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1">
                                                    <span style={{ fontWeight: 600 }}>
                                                        {t("proposals.virtualMeet")}{" "}
                                                    </span>
                                                    - {t("proposals.meetingFinCont")}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem disableGutters alignItems="center">
                                        <ListItemIcon sx={{ minWidth: 35 }}>
                                            <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1">
                                                    <span style={{ fontWeight: 600 }}>
                                                        {t("proposals.consoFinanCont")}{" "}
                                                    </span>
                                                    - {t("proposals.finanConDet")}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem disableGutters alignItems="center">
                                        <ListItemIcon sx={{ minWidth: 35 }}>
                                            <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1">
                                                    <span style={{ fontWeight: 600 }}>
                                                        {t("proposals.writtenSup")}{" "}
                                                    </span>
                                                    - {t("proposals.UnlimSup")}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    <Grid item container xs={12} spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <DatePicker
                                componentsProps={{
                                    actionBar: {
                                        actions: ["clear"],
                                    },
                                }}
                                value={valuesContRecu.initDate}
                                onChange={(newValue) => handleChangeSinceDate(newValue)}
                                inputFormat={todos.dateFormat}
                                renderInput={(params) => (
                                    <TextField
                                        fullWidth
                                        required
                                        {...params}
                                        error={isError && !valuesContRecu.initDate}
                                        onClick={(e) =>{setIsErrorFocus(false)}}
                                    />
                                )}
                                // inputProps={{
                                //     readOnly: true,
                                // }}
                                label={t("reports.initDate")}
                                views={["year", "month"]}
                                inputProps={{
                                    onKeyDown: (event) => {
                                        event.preventDefault();
                                    },
                                }}
                                onClick={(e) =>{setIsErrorFocus(false)}}
                                inputRef={(input) => { (input && isErrorFocus && isError && !valuesContRecu.initDate) && input.focus()}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <TextField
                                fullWidth
                                name="projectDiscount"
                                label={t("miscellaneous.discount")}
                                value={valuesContRecu.recuDiscount}
                                onChange={handleChangeDiscount}
                                onBlur={() =>
                                    setValuesContRecu({
                                        ...valuesContRecu,
                                        recuDiscount: parseFloat(
                                            valuesContRecu.recuDiscount === "" ? 0 : valuesContRecu.recuDiscount
                                        ).toFixed(2),
                                        recuAdditionalsDiscount: parseFloat(
                                            valuesContRecu.recuDiscount === ""
                                                ? 0
                                                : valuesContRecu.recuAdditionalsDiscount
                                        ).toFixed(2),
                                    })
                                }
                                variant="outlined"
                                type="number"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    inputProps: {
                                        min: 0,
                                        max: 100,
                                    },
                                }}
                                sx={{
                                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                        display: "none",
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <TextField
                                fullWidth
                                name="additionalsDiscount"
                                label={t("proposals.additionalsDiscount")}
                                value={valuesContRecu.recuAdditionalsDiscount}
                                onChange={handleChangeDiscount}
                                onBlur={() =>
                                    setValuesContRecu({
                                        ...valuesContRecu,
                                        recuDiscount: parseFloat(
                                            valuesContRecu.recuAdditionalsDiscount === ""
                                                ? 0
                                                : valuesContRecu.recuDiscount
                                        ).toFixed(2),
                                        recuAdditionalsDiscount: parseFloat(
                                            valuesContRecu.recuAdditionalsDiscount === ""
                                                ? 0
                                                : valuesContRecu.recuAdditionalsDiscount
                                        ).toFixed(2),
                                    })
                                }
                                variant="outlined"
                                type="number"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    inputProps: {
                                        min: 0,
                                        max: 100,
                                    },
                                }}
                                sx={{
                                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                        display: "none",
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Additionals
                                addHourFee={valuesContRecu.recuHourRate}
                                values={valuesContRecu}
                                set={setValuesContRecu}
                                setTexto={setTexto}
                                setAutoAlertType={setAutoAlertType}
                                setShow={setShow}
                                clientMarket={clientMarket}
                                sumTotal={sumTotal}
                                isError={isError}
                                isErrorFocus={isErrorFocus}
                                setIsErrorFocus={setIsErrorFocus}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </StyledContainer>
    );
};
